"use client"

import React from "react"
import { useRouter } from "next/navigation"
import TC_Wrapper from "react-tag-commander"

import { cn, GetMobilePagesPagination } from "@/lib/utils"
import { RemixIcon } from "@/components/ui/atoms"

type TPagination = {
  label: string | number
  isCurrent: boolean
  url: string
}

type TPaginationProps = {
  className?: string
  list: TPagination[]
}

const Pagination = ({ list, className }: TPaginationProps) => {
  const wrapper = TC_Wrapper.getInstance()
  const router = useRouter()
  const paginationCount = list.length
  const currentPageIndex = list.findIndex((item) => item.isCurrent)

  const navigateToPage = (url: string, label: number | string) => {
    wrapper.triggerEvent("navigation", url, {
      click: label,
      click_chapter1: url,
      click_chapter2: "undefined",
      click_chapter3: "undefined",
      click_profil: "undefined",
      click_tag: "undefined",
      click_type: "undefined",
    })
    router.push(url, { scroll: true })
  }

  const firstPage = list[0]
  const middlePages = list.slice(1, paginationCount - 1)
  const lastPage = list[paginationCount - 1]
  const mobilePages = GetMobilePagesPagination(
    list,
    currentPageIndex,
    paginationCount
  )

  return (
    <ul
      className={cn(
        "list mb-14 inline-flex items-center justify-center gap-6",
        className
      )}
    >
      {/* Previous Page Button */}
      {currentPageIndex > 0 && (
        <li className="flex aspect-square min-h-[1.5rem] min-w-[1.5rem] items-center justify-center rounded-100 text-neutral-400 transition-all ease-in-out hover:text-dark">
          <button
            onClick={() =>
              navigateToPage(
                list[currentPageIndex - 1].url,
                list[currentPageIndex - 1].label
              )
            }
            className="flex size-full appearance-none items-center justify-center  text-sm"
          >
            <RemixIcon name="ArrowLeftS" />
          </button>
        </li>
      )}

      {/* First Page for Desktop */}
      <li
        className={cn(
          "hidden aspect-square min-h-[1.5rem] min-w-[1.5rem] items-center justify-center rounded-100 text-neutral-400 lg:flex",
          firstPage.isCurrent
            ? "pointer-events-none bg-secondary-500 font-600 text-neutral-500"
            : "text-neutral-400 transition-all ease-in-out hover:text-dark"
        )}
      >
        <button
          onClick={() => navigateToPage(firstPage.url, firstPage.label)}
          disabled={firstPage.isCurrent}
          className="block size-full appearance-none text-sm"
        >
          {firstPage.label}
        </button>
      </li>

      {/* Mobile Pages */}
      {mobilePages.map((item) => (
        <li
          key={item.url}
          className={cn(
            "flex aspect-square min-h-[1.5rem] min-w-[1.5rem] items-center justify-center rounded-100 lg:hidden",
            item.isCurrent
              ? "bg-secondary-500 font-600 text-dark"
              : "text-neutral-400 transition-all ease-in-out hover:text-dark"
          )}
        >
          <button
            onClick={() => navigateToPage(item.url, item.label)}
            disabled={item.isCurrent}
            className="block size-full appearance-none text-sm"
          >
            {item.label}
          </button>
        </li>
      ))}

      {/* Ellipsis after the first page and before the current pages */}
      {currentPageIndex - 1 > 1 && (
        <li className="hidden aspect-square min-h-[1.5rem] min-w-[1.5rem] items-center justify-center text-neutral-400 lg:flex">
          <span className="text-sm text-neutral-400">...</span>
        </li>
      )}

      {/* Middle pages for Desktop */}
      {middlePages.map((item) => (
        <li
          key={item.url}
          className={cn(
            "hidden aspect-square min-h-[1.5rem] min-w-[1.5rem] items-center justify-center rounded-100 lg:flex",
            item.isCurrent
              ? "bg-secondary-500 font-600 text-dark hover:cursor-text hover:bg-secondary-500"
              : "text-neutral-400 transition-all ease-in-out hover:text-dark"
          )}
        >
          <button
            onClick={() => navigateToPage(item.url, item.label)}
            disabled={item.isCurrent}
            className="block size-full appearance-none text-sm"
          >
            {item.label}
          </button>
        </li>
      ))}

      {/* Ellipsis after the current pages and before the last page */}
      {currentPageIndex + 2 < paginationCount - 1 && (
        <li className="hidden aspect-square min-h-[1.5rem] min-w-[1.5rem] items-center justify-center lg:flex">
          <span className="text-sm text-neutral-400">...</span>
        </li>
      )}

      {/* Last Page for Desktop */}
      {paginationCount > 1 ? (
        <li
          className={cn(
            "hidden aspect-square min-h-[1.5rem] min-w-[1.5rem] items-center justify-center rounded-100 lg:flex",
            lastPage.isCurrent
              ? "pointer-events-none bg-secondary-500 font-600 text-dark"
              : "text-neutral-400 transition-all ease-in-out hover:text-dark"
          )}
        >
          <button
            onClick={() => navigateToPage(lastPage.url, lastPage.label)}
            disabled={lastPage.isCurrent}
            className="block size-full appearance-none text-sm"
          >
            {lastPage.label}
          </button>
        </li>
      ) : null}

      {/* Next Page Button */}
      {currentPageIndex < paginationCount - 1 && (
        <li className="flex aspect-square min-h-[1.5rem] min-w-[1.5rem] items-center justify-center rounded-100 text-neutral-400 transition-all ease-in-out hover:text-dark">
          <button
            onClick={() =>
              navigateToPage(
                list[currentPageIndex + 1].url,
                list[currentPageIndex + 1].label
              )
            }
            className="flex size-full appearance-none items-center justify-center  text-sm"
          >
            <RemixIcon name="ArrowRightS" />
          </button>
        </li>
      )}
    </ul>
  )
}

export { Pagination, type TPagination, type TPaginationProps }
